import React, { useMemo, useState } from 'react';
import { Button } from 'antd';
import {
  AlignedInfo,
  Drawer,
  ErrorHandler,
  Gap,
  Gutter,
  LoadingState,
  Text,
} from '@common/components';
import { useQuery } from '@common/hooks';
import { roleLevelParser } from '@institutions/services';
import { BillingCardLogQuery, BillingCardType } from '@institutions/graphql';
import { RoleLevelEnum } from '@common/graphql';
import { RatesLongTermViewDrawerCard } from './_rates-long-term-view-drawer-card';
import { getRateCardsByComputedStatus } from './_rates-long-term-view-drawer-service';

export function RatesLongTermViewDrawer({
  open,
  onClose,
  organizationUnitId = '',
  roleId = '',
  roleLevel,
}: {
  open: boolean;
  onClose: () => void;
  organizationUnitId?: string;
  roleId?: string;
  roleLevel?: RoleLevelEnum;
}) {
  const SHOWN_RATE_CARDS_MULTIPLE = 4;

  const [rateCardsQuantityToShow, setRateCardsQuantityToShow] = useState(
    SHOWN_RATE_CARDS_MULTIPLE
  );

  const { data, error, loading } = useQuery(BillingCardLogQuery, {
    skip: !organizationUnitId || !roleId || !roleLevel,
    variables: {
      organizationUnitId: organizationUnitId,
      roleId: roleId,
      roleLevel: roleLevel ?? 'not_applicable',
    },
  });

  const { inEffectCards, expiredCards } = useMemo(
    () => getRateCardsByComputedStatus(data?.billingCardLog?.billingCards),
    [data?.billingCardLog?.billingCards]
  );

  if (error) {
    return (
      <ErrorHandler
        error={error}
        log="RatesLongTermViewDrawer"
        messageType="contextual"
      />
    );
  }

  function loadMoreRateCards() {
    setRateCardsQuantityToShow(
      rateCardsQuantityToShow + SHOWN_RATE_CARDS_MULTIPLE
    );
  }

  const shouldShowLoadMoreButton =
    rateCardsQuantityToShow < expiredCards.length;

  const { organizationUnit, role } = data?.billingCardLog || {};

  const unit = organizationUnit?.department.organizationBusiness.name;
  const unitName = organizationUnit?.clinicalUnit.displayName;
  const location = organizationUnit?.name;
  const roleName = role?.name;
  const roleAbbreviation = role?.abbreviation;
  const normalizedRoleLevel = roleLevelParser(roleLevel);

  const unitData = [
    ['Unit Name', unitName],
    ['Facility', location],
    ['Unit', unit],
    ['Role', roleName],
    ['Level', normalizedRoleLevel],
  ];

  return (
    <Drawer
      open={open}
      title="View Rates"
      className="rates-long-term-view-drawer"
      onClose={() => {
        onClose();
        setRateCardsQuantityToShow(SHOWN_RATE_CARDS_MULTIPLE);
      }}
    >
      {loading ? (
        <LoadingState />
      ) : (
        <div className="rates-information-drawer">
          <AlignedInfo content={unitData} />
          <RateCardsList
            title="Enabled"
            cards={inEffectCards}
            roleAbbreviation={roleAbbreviation}
            roleLevel={roleLevel}
          />
          {!!expiredCards?.length && (
            <RateCardsList
              disabled
              title="Disabled"
              roleAbbreviation={roleAbbreviation}
              roleLevel={roleLevel}
              cards={expiredCards.slice(0, rateCardsQuantityToShow)}
            />
          )}
          {shouldShowLoadMoreButton && (
            <Gutter direction="top" size="lg">
              <Button
                data-testid="load-more-button"
                onClick={loadMoreRateCards}
              >
                Load More
              </Button>
            </Gutter>
          )}
        </div>
      )}
    </Drawer>
  );
}

function RateCardsList({
  title,
  cards,
  roleAbbreviation,
  roleLevel,
  disabled,
}: {
  title: string;
  cards: BillingCardType[];
  roleAbbreviation?: string;
  roleLevel?: RoleLevelEnum;
  disabled?: boolean;
}) {
  return (
    <div>
      <Gutter direction="vertical" size="md">
        <Text bold>{title}</Text>
      </Gutter>
      <Gap size="lg" vertical fullWidth align="stretch">
        {cards.map((card) => (
          <RatesLongTermViewDrawerCard
            key={card.id}
            rateCard={card}
            roleAbbreviation={roleAbbreviation}
            roleLevel={roleLevel}
            disabled={disabled}
          />
        ))}
      </Gap>
    </div>
  );
}
